export const SEARCH_CACHE_PREFIX = "monapplimairie-search";
export const SEARCH_CACHE_TTL = 5 * 60 * 1000;
export const RECENT_SEARCHES_KEY = "monapplimairie-recent-searches";

export function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function ensureArray(value) {
  return Array.isArray(value) ? value : [];
}

export function stripHtml(html = "") {
  return String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function truncateText(value = "", maxLength = 180) {
  const text = stripHtml(value);
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1).trim()}…`;
}

export function normalizeSearchText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

export function getSearchCacheKey(query) {
  return `${SEARCH_CACHE_PREFIX}:${normalizeSearchText(query)}`;
}

export function readSearchCache(query) {
  if (typeof window === "undefined") return null;

  try {
    const raw = sessionStorage.getItem(getSearchCacheKey(query));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed?.createdAt || !parsed?.data) return null;

    const expired = Date.now() - parsed.createdAt > SEARCH_CACHE_TTL;
    if (expired) {
      sessionStorage.removeItem(getSearchCacheKey(query));
      return null;
    }

    return parsed.data;
  } catch {
    return null;
  }
}

export function writeSearchCache(query, data) {
  if (typeof window === "undefined") return;

  try {
    sessionStorage.setItem(
      getSearchCacheKey(query),
      JSON.stringify({
        createdAt: Date.now(),
        data,
      })
    );
  } catch {
    // Cache facultatif.
  }
}

export function readRecentSearches() {
  if (typeof window === "undefined") return [];

  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
    const parsed = JSON.parse(raw);

    return ensureArray(parsed).filter(Boolean).slice(0, 8);
  } catch {
    return [];
  }
}

export function addRecentSearch(query) {
  if (typeof window === "undefined") return [];

  const clean = String(query || "").trim();
  if (!clean) return readRecentSearches();

  const previous = readRecentSearches();
  const next = [clean, ...previous.filter((item) => item !== clean)].slice(0, 8);

  try {
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
  } catch {
    // Historique facultatif.
  }

  return next;
}

export function clearRecentSearches() {
  if (typeof window === "undefined") return;

  try {
    localStorage.removeItem(RECENT_SEARCHES_KEY);
  } catch {
    // Historique facultatif.
  }
}

export function getFallbackHref(item, sectionKey) {
  if (item?.href) return item.href;

  const slugOrId = item?.slug || item?.id;

  if (sectionKey === "pages" && item?.slug) return `/page/${item.slug}`;
  if (sectionKey === "commerces" && slugOrId) return `/commerces/${slugOrId}`;

  const fallbacks = {
    news: "/actualites",
    events: "/agenda",
    services: "/services-municipaux",
    associations: "/associations",
    documents: "/documents",
    publications: "/publications",
    projets: "/projets",
  };

  return fallbacks[sectionKey] || "/";
}

export function normalizeResultItem(item, section) {
  const title =
    item?.title ||
    item?.name ||
    item?.nom ||
    item?.nom_service ||
    item?.nom_asso ||
    item?.nom_commerce ||
    item?.titre ||
    "Résultat";

  const excerpt =
    item?.excerpt ||
    item?.description ||
    item?.descriptionText ||
    item?.content ||
    item?.subtitle ||
    "";

  const meta =
    item?.meta ||
    item?.category ||
    item?.categorie ||
    item?.categorie_service ||
    item?.type_service ||
    "";

  return {
    id: item?.id || `${section.key}-${title}`,
    type: item?.type || section.key,
    sectionKey: section.key,
    title,
    excerpt: truncateText(excerpt, 190),
    meta,
    href: getFallbackHref(item, section.key),
    raw: item,
  };
}

export function uniqueResults(items = []) {
  const map = new Map();

  items.forEach((item) => {
    const key = `${item.sectionKey}-${item.id}-${item.href}-${item.title}`;
    if (!map.has(key)) map.set(key, item);
  });

  return Array.from(map.values());
}

export function normalizeSearchResponse(response, sections = []) {
  const groups = sections.map((section) => {
    const items = uniqueResults(
      ensureArray(response?.[section.key]).map((item) =>
        normalizeResultItem(item, section)
      )
    );

    return {
      ...section,
      items,
      count: items.length,
    };
  });

  const totalFromCounts = Number(response?.counts?.total || 0);
  const totalFromGroups = groups.reduce((sum, group) => sum + group.count, 0);

  return {
    raw: response,
    groups,
    total: totalFromCounts || totalFromGroups,
  };
}

export function getVisibleGroups(groups = [], activeSection = "all") {
  if (activeSection === "all") {
    return groups.filter((group) => group.count > 0);
  }

  return groups.filter((group) => group.key === activeSection);
}